import type { LoginAttempt } from '@/features/authentication/types/accountAccess';
import type { AuthTokenResult } from '@/shared/lib/authSession';
import { randomUUID } from '@/shared/lib/uuid';
import type { AuthUser } from '@/shared/types/auth';
import { create } from 'zustand';

type InFlightLoginFinalizations = ReadonlyMap<string, Promise<void>>;

type LoginFinalizeAttemptStore = {
  attempt: LoginAttempt | null;
  inFlight: InFlightLoginFinalizations;
  reserveAttempt: (
    user: AuthUser,
    token: AuthTokenResult,
    invitationToken?: string,
  ) => LoginAttempt;
  getInFlight: (key: string) => Promise<void> | undefined;
  setInFlight: (key: string, work: Promise<void>) => void;
  clearInFlight: (key: string, work: Promise<void>) => void;
  clear: () => void;
};

function sameAttempt(left: LoginAttempt, right: Omit<LoginAttempt, 'key'>): boolean {
  return (
    left.uid === right.uid &&
    left.email === right.email &&
    left.authTime === right.authTime &&
    left.provider === right.provider &&
    left.invitationToken === right.invitationToken
  );
}

/**
 * Page-lifetime state for login finalization. This store deliberately has no persistence: a reload
 * starts a new attempt, while retries and StrictMode callers in the current page share one command.
 */
export const useLoginFinalizeAttemptStore = create<LoginFinalizeAttemptStore>((set, get) => ({
  attempt: null,
  inFlight: new Map(),
  reserveAttempt: (user, token, invitationToken) => {
    const identity = {
      uid: user.uid,
      email: typeof token.claims.email === 'string' ? token.claims.email : null,
      authTime: token.authTime,
      provider: token.signInProvider,
      invitationToken: invitationToken ?? null,
    };
    const existing = get().attempt;
    if (existing && sameAttempt(existing, identity)) return existing;
    const attempt = { ...identity, key: randomUUID() };
    set({ attempt });
    return attempt;
  },
  getInFlight: (key) => get().inFlight.get(key),
  setInFlight: (key, work) => {
    const inFlight = new Map(get().inFlight);
    inFlight.set(key, work);
    set({ inFlight });
  },
  clearInFlight: (key, work) => {
    set((state) => {
      if (state.inFlight.get(key) !== work) return state;
      const inFlight = new Map(state.inFlight);
      inFlight.delete(key);
      return { inFlight };
    });
  },
  clear: () => set({ attempt: null, inFlight: new Map() }),
}));
